/**
 * DatasetForm — shared create/edit form for Dataset entities.
 *
 * Slug auto-derives from display_name on first edit (kebab-case, lowercased).
 * Once the user has typed in the slug field directly, auto-derivation stops
 * to avoid clobbering their intent. Slug regex matches the backend
 * constraint (`^[a-z0-9]([a-z0-9.-]{0,62}[a-z0-9])?$`).
 */

import { useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SLUG_REGEX, deriveSlug } from '@/lib/slug'
import {
  useCreateDataset,
  useUpdateDataset,
  type Dataset,
} from '@/hooks/useDatasets'

interface FormState {
  slug: string
  display_name: string
  description: string
}

interface DatasetFormProps {
  mode: 'create' | 'edit'
  initial?: Dataset
  onSaved: (saved: Dataset) => void
  onCancel: () => void
}

export function DatasetForm({
  mode,
  initial,
  onSaved,
  onCancel,
}: DatasetFormProps): React.JSX.Element {
  const createMutation = useCreateDataset()
  const updateMutation = useUpdateDataset()

  const [form, setForm] = useState<FormState>({
    slug: initial?.slug ?? '',
    display_name: initial?.display_name ?? '',
    description: initial?.description ?? '',
  })
  // In create mode, auto-fill slug from display_name until the user has
  // explicitly edited the slug field at least once.
  const [slugDirty, setSlugDirty] = useState(mode === 'edit')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const isPending = createMutation.isPending || updateMutation.isPending
  const slugValid = form.slug === '' || SLUG_REGEX.test(form.slug)
  const canSubmit =
    form.slug.length > 0 &&
    form.display_name.length > 0 &&
    slugValid &&
    !isPending

  function updateDisplayName(value: string): void {
    setForm((prev) => ({
      ...prev,
      display_name: value,
      slug: slugDirty ? prev.slug : deriveSlug(value),
    }))
    setSubmitError(null)
  }

  function updateSlug(value: string): void {
    setForm((prev) => ({ ...prev, slug: value }))
    setSlugDirty(true)
    setSubmitError(null)
  }

  function updateDescription(value: string): void {
    setForm((prev) => ({ ...prev, description: value }))
    setSubmitError(null)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    if (!canSubmit) return

    if (mode === 'create') {
      const result = await createMutation.mutateAsync({
        data: {
          slug: form.slug,
          display_name: form.display_name,
          description: form.description,
        },
      })
      const created = (result as { data: Dataset; status: number }).data
      onSaved(created)
      return
    }

    if (!initial) {
      setSubmitError('Cannot edit: dataset not loaded')
      return
    }
    const result = await updateMutation.mutateAsync({
      datasetId: initial.id,
      data: {
        slug: form.slug,
        display_name: form.display_name,
        description: form.description,
      },
    })
    const updated = (result as { data: Dataset; status: number }).data
    onSaved(updated)
  }

  const mutationError = createMutation.error ?? updateMutation.error ?? null
  const mutationErrorMessage =
    mutationError instanceof Error ? mutationError.message : null

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e)
      }}
      className="space-y-6"
    >
      <div className="space-y-2">
        <Label htmlFor="display-name">Display name</Label>
        <Input
          id="display-name"
          value={form.display_name}
          onChange={(e) => {
            updateDisplayName(e.target.value)
          }}
          placeholder="SQL Eval January 2026"
          maxLength={120}
          disabled={isPending}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          value={form.slug}
          onChange={(e) => {
            updateSlug(e.target.value)
          }}
          placeholder="sql-eval-jan-2026"
          maxLength={64}
          disabled={isPending}
          aria-invalid={!slugValid}
          aria-describedby={slugValid ? 'slug-help' : 'slug-error'}
          required
        />
        {slugValid ? (
          <p id="slug-help" className="text-xs text-muted-foreground">
            Used in URLs and API payloads. Lowercase alnum + hyphens/dots, ≤64 chars.
          </p>
        ) : (
          <p id="slug-error" className="text-sm text-destructive">
            Slug must be lowercase alnum + hyphens/dots, start and end alphanumeric,
            ≤64 chars.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          value={form.description}
          onChange={(e) => {
            updateDescription(e.target.value)
          }}
          placeholder="What's in this dataset and what's it for?"
          rows={4}
          disabled={isPending}
        />
      </div>

      {mutationErrorMessage && (
        <p role="alert" className="text-sm text-destructive">
          {mutationErrorMessage}
        </p>
      )}
      {submitError && (
        <p role="alert" className="text-sm text-destructive">
          {submitError}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {mode === 'create' ? 'Create dataset' : 'Save changes'}
        </Button>
      </div>
    </form>
  )
}
